/**
 * Settings AI Control — task-flow + AI Team object + layered readiness.
 * MCP is a community directory + vault bind. Not a model. Not hardware Skills.
 * Callable is only true after a live list-tools. Directory ≠ connected ≠ invocable.
 */
import { useEffect, useMemo, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { html, Ico, go, hashSearchParams } from "./ui.js";
import { path, useAction, useApi } from "./api.js";
import { useSession } from "./session.js";
import { useCatalogue, useEffective } from "./models-api.js";
import { ModelSettingsDesk } from "./model-settings-desk.js";
import { ViewportSheet } from "./viewport-sheet.js";
import { aiReadinessOf } from "./ai-readiness.js";
import { ObjectCard, WorkbenchHead } from "./object-workbench.js";
import { ReadinessState, Receipt, TechnicalDetails } from "./layered-console.js";

export { aiReadinessOf };

const LANES = [
  { id: "models", label: "Models", say: "Local and API brains." },
  { id: "playbooks", label: "Playbooks", say: "Markdown the team follows." },
  { id: "mcp", label: "Tools", say: "Servers the team may call. Not a model." },
];

function directorySayOf(source, reason) {
  const src = String(source || "").trim();
  const why = String(reason || "").trim();
  const srcSay = src === "mcp_registry" ? "Official registry"
    : src === "unavailable" ? "Could not be read"
    : src === "disabled" ? "Off"
    : "";
  const whySay = why === "registry_empty" ? "The list came back empty."
    : (why && !/^[a-z0-9_]+$/i.test(why) ? why : "");
  return { srcSay, whySay };
}

function asRows(pack, keys) {
  if (!pack || typeof pack !== "object") return [];
  for (const k of keys) {
    const rows = pack[k];
    if (Array.isArray(rows)) return rows;
  }
  return [];
}

function downloadMd(name, body) {
  const safe = String(name || "playbook").replace(/[^\w.-]+/g, "-").slice(0, 80);
  const blob = new Blob([String(body || "")], { type: "text/markdown;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${safe}.md`;
  a.click();
  URL.revokeObjectURL(url);
}

function laneFromHash(fallback) {
  const id = String(hashSearchParams().get("lane") || "").trim().toLowerCase();
  if (id === "verify") return "mcp";
  return LANES.some((l) => l.id === id) ? id : fallback;
}

function Sheet({ title, onClose, children }) {
  return html`<${ViewportSheet} title=${title} dock="right" onClose=${onClose}>${children}<//>`;
}

export function AiStudioDesk({ toast }) {
  const { me } = useSession();
  const role = String((me && me.role) || "").toLowerCase();
  const isUser = role === "user";
  const writeOk = !isUser && (role === "admin" || role === "superadmin" || role === "sa_staff");
  const fallbackLane = isUser ? "playbooks" : "models";
  const [lane, setLane] = useState(() => laneFromHash(fallbackLane));
  const [sheet, setSheet] = useState(null);
  const skills = useApi(path("get__ai_platform_skills"));
  const mcp = useApi(path("get__ai_platform_mcp-servers"));
  const mine = useCatalogue("mine");
  const effective = useEffective("");
  const { run, busy } = useAction();

  const skillRows = asRows(skills.data, ["items", "skills"]);
  const mcpRows = asRows(mcp.data, ["items", "servers"]);
  const directory = Array.isArray(mcp.data && mcp.data.directory) ? mcp.data.directory : [];
  const starters = Array.isArray(mcp.data && mcp.data.starters) ? mcp.data.starters : [];
  const directorySource = String((mcp.data && mcp.data.directory_source) || "");
  const directoryReason = String((mcp.data && mcp.data.directory_reason) || "");
  const audience = String((skills.data && skills.data.audience) || (mcp.data && mcp.data.audience) || "");
  const canEdit = writeOk && (audience === "sa_edit" || role === "superadmin");

  const [draft, setDraft] = useState({ name: "", markdown: "", skill_id: "" });
  const [bind, setBind] = useState({ name: "", vault: "" });
  const [q, setQ] = useState("");
  const [toolPack, setToolPack] = useState({});
  const [receipt, setReceipt] = useState(null);

  useEffect(() => {
    const sync = () => {
      const next = laneFromHash(fallbackLane);
      setLane((cur) => (cur === next ? cur : next));
    };
    sync();
    addEventListener("hashchange", sync);
    return () => removeEventListener("hashchange", sync);
  }, [fallbackLane]);

  useEffect(() => {
    if (sheet && sheet.kind === "playbook" && sheet.row) {
      setDraft({
        name: String(sheet.row.name || ""),
        markdown: String(sheet.row.markdown || sheet.row.description || ""),
        skill_id: String(sheet.row.skill_id || ""),
      });
    }
    if (sheet && (sheet.kind === "mcp" || sheet.kind === "connect") && sheet.row) {
      setBind({ name: String(sheet.row.name || ""), vault: "" });
    }
  }, [sheet]);

  const models = useMemo(() => (mine.models || []).filter(Boolean), [mine.models]);
  const needle = q.trim().toLowerCase();
  const installedNames = useMemo(
    () => new Set(mcpRows.map((r) => String(r.name || "").trim().toLowerCase()).filter(Boolean)),
    [mcpRows],
  );
  const installed = useMemo(() => {
    const rows = mcpRows.filter((r) => !needle || String(r.name || "").toLowerCase().includes(needle));
    return rows;
  }, [mcpRows, needle]);
  const community = useMemo(() => {
    const rows = (directory.length ? directory : []).filter((row) => {
      const name = String(row.name || "").trim();
      if (!name) return false;
      if (installedNames.has(name.toLowerCase())) return false;
      if (needle && !name.toLowerCase().includes(needle) && !String(row.hint || "").toLowerCase().includes(needle)) {
        return false;
      }
      return true;
    });
    return rows;
  }, [directory, installedNames, needle]);
  const suggested = useMemo(() => {
    const rows = (starters.length ? starters : []).filter((row) => {
      const name = String(row.name || "").trim();
      if (!name) return false;
      if (installedNames.has(name.toLowerCase())) return false;
      if (needle && !name.toLowerCase().includes(needle) && !String(row.hint || "").toLowerCase().includes(needle)) {
        return false;
      }
      return true;
    });
    return rows;
  }, [starters, installedNames, needle]);

  const ready = useMemo(
    () => aiReadinessOf({
      models,
      effective: effective.effective || effective,
      skillRows,
      mcpRows,
      toolPack,
    }),
    [models, effective, skillRows, mcpRows, toolPack],
  );
  const dirSay = directorySayOf(directorySource, directoryReason);

  const savePlaybook = async () => {
    if (!canEdit) return toast && toast("Saving playbooks needs an AI admin seat.", "warn");
    if (!draft.name.trim()) return toast && toast("Name the playbook first.", "warn");
    const out = await run(path("put__ai_platform_skills"), {
      method: "PUT",
      body: {
        name: draft.name.trim(),
        markdown: draft.markdown,
        scope: (role === "superadmin" || role === "sa_staff") ? "global" : "tenant",
        enabled: true,
        skill_id: draft.skill_id || undefined,
      },
    });
    if (!out || out.error) {
      toast && toast((out && out.error) || "Playbook was not saved.", "bad");
      return;
    }
    toast && toast("Playbook saved", "go");
    setReceipt({
      title: "Playbook saved",
      accepted: draft.name.trim(),
      effective: (role === "superadmin" || role === "sa_staff") ? "global" : "tenant",
      evidence: "Live skills write",
      at: new Date().toISOString(),
      next: "Open Tools and list them — installed is not callable.",
      tone: "go",
    });
    skills.reload && skills.reload();
    setSheet(null);
  };

  const connectMcp = async () => {
    if (!canEdit) return toast && toast("Connecting tools needs an AI admin seat.", "warn");
    if (!bind.name.trim() || !bind.vault.trim()) {
      return toast && toast("Name and a vault link are required.", "warn");
    }
    if (!bind.vault.trim().startsWith("vault://")) {
      return toast && toast("Use a vault link, not a raw web address.", "warn");
    }
    const out = await run(path("put__ai_platform_mcp-servers"), {
      method: "PUT",
      body: {
        name: bind.name.trim(),
        url: bind.vault.trim(),
        enabled: true,
        scope: (role === "superadmin" || role === "sa_staff") ? "global" : "tenant",
      },
    });
    if (!out || out.error) {
      toast && toast((out && out.error) || "Connection was refused.", "bad");
      return;
    }
    toast && toast("Tool server added · " + bind.name.trim(), "go");
    setReceipt({
      title: "Tool server connected",
      accepted: bind.name.trim(),
      effective: "vault bind (no raw URL)",
      evidence: "Live tool catalogue write",
      at: new Date().toISOString(),
      next: "List tools on this server — directory ≠ callable.",
      tone: "go",
    });
    mcp.reload && mcp.reload();
    setSheet(null);
  };

  const listMcpTools = async (row) => {
    const id = String((row && (row.mcp_id || row.id)) || "").trim();
    if (!id) return;
    if (!canEdit) return toast && toast("Listing tools needs an AI admin seat.", "warn");
    const out = await run(
      path("post__ai_platform_mcp-servers_p_tools_list", { pathParams: [id] }),
      { method: "POST", body: {} },
    );
    if (!out || out.error) {
      setToolPack((prev) => ({ ...prev, [id]: { status: "error", tools: [] } }));
      toast && toast((out && out.error) || "Tools were not listed.", "bad");
      return;
    }
    const names = asRows(out, ["tools", "items", "listed"]).map((t) =>
      String((t && (t.name || t.id || t.tool_name)) || t || "").trim()
    ).filter(Boolean);
    setToolPack((prev) => ({ ...prev, [id]: { status: "ready", tools: names } }));
    setReceipt({
      title: "Tools listed",
      accepted: String((row && row.name) || id),
      effective: names.length + " tool(s)",
      evidence: "Live list-tools · Connected ≠ Callable until listed",
      at: new Date().toISOString(),
      next: names.length ? "Verify ladder should advance Available → Callable." : "Server returned an empty tool list.",
      tone: names.length ? "go" : "warn",
    });
    setSheet({ kind: "tools", row: { ...row, mcp_id: id }, names });
  };

  const pickLane = (id) => {
    const next = id === "verify" ? "mcp" : id;
    setLane(next);
    go("/settings/ai?lane=" + next);
  };

  const brainLabel = (effective.effective && effective.effective.label)
    || (effective.label)
    || (models[0] && (models[0].label || models[0].name))
    || "";
  const headTitle = lane === "models" ? "Local and API brains"
    : lane === "playbooks" ? "Playbooks"
    : "Watch team tools";
  const headSay = lane === "playbooks"
      ? (canEdit
        ? "Markdown the team follows. Saving a playbook does not dispatch."
        : "Read the markdown the team follows. Edits stay with site staff.")
      : (canEdit
        ? "Add a server, then list its tools. A directory card is not callable."
        : "See which servers are on. A directory card is not callable. Edits stay with site staff.");
  const toolTitle = ready.installedN
    ? (ready.installedN + (ready.installedN === 1 ? " server" : " servers"))
    : "No tool servers yet";
  const headObjectTitle = lane === "playbooks"
    ? (skillRows.length ? (skillRows.length + " playbooks") : "No playbooks yet")
    : lane === "mcp"
      ? toolTitle
      : (brainLabel || "No Watch brain yet");
  const headMeta = lane === "mcp"
    ? ((canEdit ? "Can edit" : "View") + " · " + (
      ready.callable ? (ready.listedN + " callable")
        : (ready.installedN ? (ready.installedN + " installed") : "None installed")
    ))
    : lane === "playbooks"
      ? (canEdit ? "Can edit" : "View")
      : (brainLabel ? "In use" : "None pinned");
  const toolsNext = ready.callable
    ? "Listed tools are callable. A community card still is not."
    : (ready.connected
      ? "Installed is not callable until list-tools returns names."
      : "No tool server on this site. A community card is not installed.");
  const statusOn = lane === "models" ? !!brainLabel
    : lane === "mcp" ? !!ready.callable
    : !!ready.verified;
  const statusLabel = lane === "models" ? (brainLabel ? "Answering" : "Open")
    : lane === "mcp" ? (ready.callable ? "Callable" : (ready.connected ? "Installed" : "Off"))
    : (ready.verified ? "Verified" : "Open");

  return html`<div className="ai-studio ai-control house-rack obj-wb" data-cmc-ai-studio="1"
      data-cmc-ai-control="1093" data-lane=${lane}
      data-ready-callable=${ready.callable ? "1" : "0"}
      data-ready-verified=${ready.verified ? "1" : "0"}
      data-cmc-ai-receipt=${receipt ? "1" : "0"}>
    ${lane === "models" ? null : html`<${WorkbenchHead}
      eyebrow="AI Control · Runtime"
      title=${headTitle}
      say=${headSay}
      object=${html`<${ObjectCard} kind="AI team" title=${headObjectTitle}
        meta=${headMeta}
        status=${{ on: statusOn, label: statusLabel }}/>`}
    />`}

    <ol className="ai-control__prep obj-wb__prep" aria-label="Team lanes">
      ${LANES.map((step) => html`<li key=${step.id} data-on=${lane === step.id ? "1" : "0"}>
        <button type="button" onClick=${() => pickLane(step.id)}>
          <b>${step.label}</b>${step.id === "mcp" ? html`<small>${step.say}</small>` : null}
        </button>
      </li>`)}
    </ol>

    ${receipt ? html`<${Receipt} title=${receipt.title} accepted=${receipt.accepted}
      effective=${receipt.effective} evidence=${receipt.evidence} at=${receipt.at}
      next=${receipt.next} tone=${receipt.tone || "go"}/>` : null}

    ${lane === "playbooks" ? html`<section className="ai-studio__playbooks" aria-label="Playbooks">
      <div className="ai-studio__grid">
      ${canEdit ? html`<button type="button" className="ai-studio__card ai-studio__card--new"
        onClick=${() => setSheet({ kind: "playbook", row: { name: "", markdown: "" } })}>
        <${Ico} d="plus" w=${16}/><b>New playbook</b><small>Markdown · saved to this site</small></button>` : null}
      ${skillRows.length ? skillRows.map((row) => html`<article key=${row.skill_id || row.name} className="ai-studio__card">
        <b>${row.name || "Untitled"}</b>
        <small>${row.new ? "New" : (row.enabled === false ? "Off" : "Ready")}</small>
        <div className="ai-studio__card-actions">
          <button type="button" className="btn btn--ghost btn--sm"
            onClick=${() => downloadMd(row.name, row.markdown || row.description)}>Save .md</button>
          ${canEdit ? html`<button type="button" className="btn btn--sm"
            onClick=${() => setSheet({ kind: "playbook", row })}>Edit</button>` : null}
        </div>
      </article>`) : html`<div className="house-rack__empty">
        <b>${skills.loading ? "Reading playbooks…" : "No playbooks yet"}</b>
        <p>${skills.loading
          ? "The site list is still arriving."
          : (canEdit ? "Write the first markdown the team should follow." : "Site staff add the first playbook.")}</p>
        ${!skills.loading && canEdit ? html`<button type="button" className="btn btn--go btn--sm"
          onClick=${() => setSheet({ kind: "playbook", row: { name: "", markdown: "" } })}>New playbook</button>` : null}
      </div>`}
      </div>
    </section>` : null}

    ${lane === "mcp" ? html`<section className="ai-studio__mcp" aria-label="Tools">
      ${installed.length ? html`<p className="ai-studio__kicker">Installed</p>` : html`<div className="house-rack__empty">
        <b>${mcp.loading ? "Reading servers…" : "No tool server on this site"}</b>
        <p>${mcp.loading ? "The site list is still arriving." : "A community card below is not installed."}</p>
      </div>`}
      <div className="ai-studio__grid">
        ${installed.map((row) => {
          const id = String(row.mcp_id || row.id || "");
          const pack = toolPack[id] || {};
          const st = pack.status || (row.enabled === false ? "off" : (row.status || "installed"));
          const n = (pack.tools || []).length;
          return html`<article key=${id || row.name} className="ai-studio__card" data-on=${st === "off" ? "0" : "1"}>
            <div className="ai-studio__card-top">
              <i className="ai-studio__dot" data-st=${st} aria-hidden="true"></i>
              <b>${row.name}</b>
            </div>
            <small>${st === "ready" ? (n + " tools listed · callable") : (st === "error" ? "Could not list tools" : (st === "off" ? "Off" : "Installed · list to prove tools"))}</small>
            ${canEdit ? html`<div className="ai-studio__card-actions">
              <button type="button" className="btn btn--ghost btn--sm" disabled=${busy}
                onClick=${() => listMcpTools(row)}>List tools</button>
            </div>` : null}
          </article>`;
        })}
      </div>
      <p className="note">Installed servers expose tools after a list. A model picker never turns a server on. Callable stays off until the list returns names.</p>
      <div className="ai-studio__mcp-bar">
        <input className="inp" value=${q} onInput=${(e) => setQ(e.target.value)}
          placeholder="Search the community directory"/>
        ${canEdit ? html`<button type="button" className="btn btn--sm"
          onClick=${() => setSheet({ kind: "mcp", row: { name: "" } })}>Add custom</button>` : null}
      </div>
      <p className="ai-studio__kicker">Community</p>
      <div className="ai-studio__grid">
        ${community.length ? community.map((row) => html`<article key=${row.id || row.name} className="ai-studio__card">
          <div className="ai-studio__card-top">
            <i className="ai-studio__dot" data-st="off" aria-hidden="true"></i>
            <b>${row.name}</b>
            <em className="ai-studio__chip">${row.transport === "host" ? "On this host" : "Remote"}</em>
          </div>
          <small>${row.hint || "Listed on the official registry"}</small>
          ${canEdit ? html`<button type="button" className="btn btn--sm"
            onClick=${() => setSheet({ kind: "mcp", row })}>Add</button>` : null}
        </article>`) : html`<div className="house-rack__empty">
          <b>${mcp.loading ? "Reading the official registry…"
            : (needle ? "No server matches"
              : (directorySource === "unavailable" ? "Official registry could not be read"
                : (directorySource === "disabled" ? "Community directory is off"
                  : "No community servers on the allowlist")))}</b>
          <p>${mcp.loading
            ? "Community names are still arriving from the official registry."
            : (needle ? "Clear the search, or add a custom vault link."
              : (directorySource === "unavailable"
                ? "Empty stays empty. We will not invent a shelf."
                : (directoryReason === "registry_empty"
                  ? "The registry answered, and none of the fetched names passed the allowlist."
                  : "Add a vault:// server if this site already owns one.")))}</p>
          ${!mcp.loading && canEdit ? html`<button type="button" className="btn btn--sm"
            onClick=${() => setSheet({ kind: "mcp", row: { name: "" } })}>Add custom</button>` : null}
        </div>`}
      </div>
      ${suggested.length ? html`<p className="ai-studio__kicker">Suggested names · not the live registry</p>
      <div className="ai-studio__grid">
        ${suggested.map((row) => html`<article key=${"s-" + (row.id || row.name)} className="ai-studio__card">
          <div className="ai-studio__card-top">
            <i className="ai-studio__dot" data-st="off" aria-hidden="true"></i>
            <b>${row.name}</b>
            <em className="ai-studio__chip">Suggested</em>
          </div>
          <small>${row.hint || "A vault name only"}</small>
          ${canEdit ? html`<button type="button" className="btn btn--sm"
            onClick=${() => setSheet({ kind: "mcp", row })}>Add</button>` : null}
        </article>`)}
      </div>` : null}
      <${TechnicalDetails} title="Tool honesty">
        <div data-cmc-studio-runtime="1">
          <${ReadinessState} steps=${ready.steps} next=${toolsNext}/>
        </div>
        <p className="note">${dirSay.srcSay ? ("Directory · " + dirSay.srcSay + ".") : "Directory not named."} ${dirSay.whySay || "A card in the community list is not installed and not callable until list-tools succeeds."}</p>
        <p className="note">These lanes are optional. Policy Kernel is the Watch fence — not this list.</p>
      <//>
    </section>` : null}

    ${lane === "models" ? html`<section className="ai-studio__brains" aria-label="Models" data-cmc-ai-brains="1">
      <${ModelSettingsDesk} toast=${toast} embedded=${true}/>
    </section>` : null}

    <${AnimatePresence}>
      ${sheet && sheet.kind === "playbook" ? html`<${Sheet} title=${draft.skill_id ? "Edit playbook" : "New playbook"}
        onClose=${() => setSheet(null)}>
        <label className="ai-studio__select"><span>Name</span>
          <input className="inp" value=${draft.name} onInput=${(e) => setDraft({ ...draft, name: e.target.value })}
            placeholder="Night patrol"/>
        </label>
        <label className="ai-studio__select"><span>Markdown</span>
          <textarea className="inp ai-studio__md" rows=${14} value=${draft.markdown}
            onInput=${(e) => setDraft({ ...draft, markdown: e.target.value })}
            placeholder=${"# When an alarm fires\n\n1. Open the camera\n2. Call the nearest responder"}/>
        </label>
        <div className="btn-row">
          <button type="button" className="btn btn--ghost btn--sm"
            onClick=${() => downloadMd(draft.name || "playbook", draft.markdown)}>Save .md</button>
          ${canEdit ? html`<button type="button" className="btn btn--go btn--sm" disabled=${busy}
            onClick=${savePlaybook}>${busy ? "Saving…" : "Save to site"}</button>` : null}
        </div>
      <//>` : null}
      ${sheet && (sheet.kind === "mcp" || sheet.kind === "connect") ? html`<${Sheet} title=${bind.name ? ("Add " + bind.name) : "Add a tool server"}
        onClose=${() => setSheet(null)}>
        <p className="note">Store a vault:// link, never a public address. Listing tools is what makes them callable.</p>
        <label className="ai-studio__select"><span>Name</span>
          <input className="inp" value=${bind.name} onInput=${(e) => setBind({ ...bind, name: e.target.value })}
            placeholder="GitHub"/>
        </label>
        <label className="ai-studio__select"><span>Vault</span>
          <input className="inp" value=${bind.vault} onInput=${(e) => setBind({ ...bind, vault: e.target.value })}
            placeholder="vault://ops/egress/github"/>
        </label>
        <button type="button" className="btn btn--go btn--sm" disabled=${busy} onClick=${connectMcp}>
          ${busy ? "Adding…" : "Add server"}</button>
      <//>` : null}
      ${sheet && sheet.kind === "tools" ? html`<${Sheet} title=${"Tools · " + ((sheet.row && sheet.row.name) || "server")}
        onClose=${() => setSheet(null)}>
        <p className="note">Discovered from the server. This list is not a model capability.</p>
        ${(sheet.names || []).length
          ? html`<ul className="ai-studio__tools">${sheet.names.map((n) => html`<li key=${n}>${n}</li>`)}</ul>`
          : html`<p className="note">No tools listed.</p>`}
      <//>` : null}
    <//>
  </div>`;
}

export default AiStudioDesk;
